import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Place } from '@mui/icons-material';

const HotspotFormDialog = ({ open, onClose, onSubmit, hotspot, isEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    latitude: '',
    longitude: '',
    city: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (hotspot) {
      setFormData({
        name: hotspot.name || '',
        latitude: hotspot.latitude || '',
        longitude: hotspot.longitude || '',
        city: hotspot.city || '',
      });
    } else {
      resetForm();
    }
    setErrors({});
  }, [hotspot, open]);

  const resetForm = () => {
    setFormData({
      name: '',
      latitude: '',
      longitude: '',
      city: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Hotspot name is required';
    }
    if (!formData.latitude.trim()) {
      newErrors.latitude = 'Latitude is required';
    }
    if (!formData.longitude.trim()) {
      newErrors.longitude = 'Longitude is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#374151',
        }}
      >
        <Place sx={{ color: '#52ad83' }} />
        {isEdit ? 'Edit Hotspot' : 'Add New Hotspot'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hotspot Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="Enter hotspot name"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  placeholder="Enter city"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleChange}
                  error={!!errors.latitude}
                  helperText={errors.latitude}
                  placeholder="e.g., 40.7128"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleChange}
                  error={!!errors.longitude}
                  helperText={errors.longitude}
                  placeholder="e.g., -74.0060"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#52ad83',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
            boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
              boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
            },
          }}
        >
          {isEdit ? 'Update Hotspot' : 'Create Hotspot'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HotspotFormDialog;
